import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { initPostHog, resetUser } from '@/lib/posthog';
import { useAuth } from '@/contexts/AuthContext';
import { useTracking } from '@/hooks/useTracking';
import { captureUTMFromURL, getUTMParams } from '@/hooks/useUTMCapture';

interface TrackingProviderProps {
  children: React.ReactNode;
}

export function TrackingProvider({ children }: TrackingProviderProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { trackPageView, trackAuthEvent, track } = useTracking();
  const qrLandedFired = useRef(false);

  // Initialize PostHog and capture UTM params on mount
  useEffect(() => {
    initPostHog();
    const utm = captureUTMFromURL();
    // Fire a dedicated event when someone lands via QR scan
    if (utm?.utm_medium === 'qr' && !qrLandedFired.current) {
      qrLandedFired.current = true;
      track('qr_scan_landed', { ...utm }, { persistToDb: true });
    }
  }, []);

  // Track page views on route change
  useEffect(() => {
    const pageName = getPageName(location.pathname);
    trackPageView(pageName, { path: location.pathname });
  }, [location.pathname, trackPageView]);

  // Track sign out
  useEffect(() => {
    if (!user) {
      resetUser();
    }
  }, [user]);

  return <>{children}</>;
}

// Map routes to readable page names
function getPageName(pathname: string): string {
  const routes: Record<string, string> = {
    '/': 'Home',
    '/interview': 'Interview',
    '/builder': 'Resume Builder',
    '/career-dna': 'Career DNA Assessment',
    '/job-hunt': 'Job Hunt',
    '/auth': 'Authentication',
    '/admin': 'Admin Dashboard',
    '/institution': 'Institution Dashboard',
    '/pricing': 'Pricing',
    '/about': 'About',
    '/contact': 'Contact',
  };

  // Check exact match first
  if (routes[pathname]) return routes[pathname];

  // Check prefix matches
  for (const [route, name] of Object.entries(routes)) {
    if (pathname.startsWith(route) && route !== '/') {
      return name;
    }
  }

  return 'Unknown Page';
}

export default TrackingProvider;
